<template>
  <nav
    class="navbar navbar-expand-lg livratos-navbar"
    :class="{ 'is-scrolled': isScrolled }"
  >
    <div class="container nav-inner">
      <a class="navbar-brand d-flex align-items-center" href="/">
        <img
          :src="isScrolled ? logoLight : logoDark"
          alt="Livratos"
          class="brand-logo"
        />
      </a>

      <button
        class="navbar-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#livratosNavbar"
        aria-controls="livratosNavbar"
        aria-expanded="false"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <div class="collapse navbar-collapse" id="livratosNavbar">
        <ul class="navbar-nav ms-auto mb-2 mb-lg-0">
          <li class="nav-item">
            <RouterLink
              to="/"
              class="nav-link"
              :class="{ active: route.path === '/' }"
            >
              Accueil
            </RouterLink>
          </li>

          <li class="nav-item">
            <RouterLink
              to="/about"
              class="nav-link"
              :class="{ active: route.path === '/about' }"
            >
              À propos
            </RouterLink>
          </li>

          <li class="nav-item dropdown">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              id="solutionsDropdown"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Solutions
            </a>
            <ul class="dropdown-menu" aria-labelledby="solutionsDropdown">
              <li>
                <RouterLink
                  to="/livraisons-fournisseurs-btp"
                  class="dropdown-item"
                >
                  Fournisseurs
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/missions-pour-livreurs" class="dropdown-item">
                  Livreurs
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/livraison-sur-chantier" class="dropdown-item">
                  Artisans
                </RouterLink>
              </li>
            </ul>
          </li>
          <li class="nav-item">
            <RouterLink
              to="/blog"
              class="nav-link"
              :class="{ active: route.path === '/blog' }"
            >
              Actualités
            </RouterLink>
          </li>

          <li class="nav-item">
            <RouterLink
              to="/contact"
              class="nav-link"
              :class="{ active: route.path === '/contact' }"
            >
              Contact
            </RouterLink>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute } from "vue-router";

import logoDark from "@/assets/brand/logo-nav-black.webp";
import logoLight from "@/assets/brand/logo-nav-white.webp";

const isScrolled = ref(false);
const route = useRoute();
const COLLAPSE_ID = "livratosNavbar";

const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

const closeMobileMenu = () => {
  const collapseEl = document.getElementById(COLLAPSE_ID);
  const toggler = document.querySelector(".navbar-toggler");
  if (!collapseEl || !collapseEl.classList.contains("show")) return;
  collapseEl.classList.remove("show");
  if (toggler) toggler.setAttribute("aria-expanded", "false");
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
  handleScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});

watch(
  () => route.fullPath,
  () => {
    closeMobileMenu();
  }
);
</script>

<style lang="scss" scoped>
.livratos-navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1030;

  background-color: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: none;

  .nav-inner {
    height: 72px;
    display: flex;
    align-items: center;
  }

  .brand-logo {
    width: 150px;
    height: auto;
    display: block;
  }

  .nav-link {
    color: $color-text !important;
    font-weight: 500;
    font-size: 0.97rem;
    padding-left: 1rem !important;
    padding-right: 1rem !important;

    &.active {
      color: $color-primary !important;
    }

    &:hover {
      color: $color-primary !important;
    }
  }

  .dropdown-menu {
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    font-size: 0.95rem;
  }

  .dropdown-item {
    color: $color-text-grey;

    &:hover {
      color: $color-primary;
      background-color: lighten($color-primary, 42%);
    }
  }

  .navbar-toggler {
    border: none;
    padding: 0.25rem 0.5rem;

    &:focus {
      box-shadow: none;
    }
  }

  .navbar-toggler-icon {
    background-image: none !important;
    width: 24px;
    height: 2px;
    background-color: $color-primary;
    border-radius: 999px;
    position: relative;
    display: inline-block;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: $color-primary;
      border-radius: 999px;
    }

    &::before {
      top: -7px;
    }

    &::after {
      top: 7px;
    }
  }
}

.livratos-navbar.is-scrolled {
  background-color: #000000;
  border-bottom-color: #000000;

  .nav-link {
    color: #ffffff !important;

    &.active {
      color: $color-primary !important;
    }

    &:hover {
      color: $color-primary !important;
    }
  }

  .dropdown-menu {
    background-color: #111;
    border: 1px solid rgba(255, 255, 255, 0.06);
  }

  .dropdown-item {
    color: #f5f5f5;

    &:hover {
      color: $color-primary;
      background-color: rgba($color-primary, 0.15);
    }
  }
}
@media (max-width: 991.98px) {
  .livratos-navbar {
    .nav-inner {
      height: 72px;
    }

    .navbar-collapse {
      position: absolute;
      top: 72px;
      left: 0;
      right: 0;
      background-color: white;
      padding: 0.75rem 1.25rem 1rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    .navbar-nav {
      width: 100%;
      align-items: flex-start;
      gap: 0.25rem;
    }

    .nav-link {
      width: 100%;
      padding-left: 0 !important;
      padding-right: 0 !important;
      padding-top: 0.35rem;
      padding-bottom: 0.35rem;
    }

    .dropdown-menu {
      position: static;
      float: none;
      box-shadow: none;
      border: none;
      background-color: transparent;
      padding: 0.25rem 0 0.25rem 1rem;
      margin-top: 0;
    }

    .dropdown-item {
      padding-left: 0;
    }
  }

  .livratos-navbar.is-scrolled {
    .navbar-collapse {
      background-color: black;
      border-bottom-color: rgba(255, 255, 255, 0.08);
    }
  }
}
</style>
